import { useEffect, useState } from "react";
import { Armchair, Wifi, ShowerHead, UtensilsCrossed, Wine, Coffee, BedDouble, Baby, Briefcase, BatteryCharging, CheckCircle2, Settings2, Flag, Sparkles, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* Airport Lounges directory + "My Lounge Access" personalization.
   Editorial cream + gold treatment matching /countries and /destinations aesthetic. */

const AMENITY_ICONS = {
  wifi: Wifi, showers: ShowerHead, hot_food: UtensilsCrossed, bar: Wine, cocktails: Wine,
  wine: Wine, coffee: Coffee, sleeping_rooms: BedDouble, quiet_area: BedDouble,
  family_room: Baby, kids_area: Baby, workspaces: Briefcase, charging: BatteryCharging,
};
const AMENITY_LABELS = {
  wifi: "Wi-Fi", hot_food: "Hot food", snacks: "Snacks", bar: "Bar", wine: "Wine", cocktails: "Cocktails",
  coffee: "Coffee", showers: "Showers", restrooms: "Restrooms", sleeping_rooms: "Sleep rooms",
  quiet_area: "Quiet area", workspaces: "Workspaces", charging: "Charging", family_room: "Family room",
  kids_area: "Kids area", tv: "TV", flight_screens: "Flight screens", newspapers: "News",
  prayer_room: "Prayer room", spa: "Spa", smoking_area: "Smoking", outdoor_terrace: "Terrace",
};

function LoungeCard({ l, myPrograms, onReport }) {
  const likely = l.programs?.some((p) => myPrograms.includes(p));
  return (
    <div className={`relative rounded-2xl overflow-hidden bg-white transition-all hover:-translate-y-0.5 hover:shadow-lg ${likely ? "border-2 border-emerald-500 shadow-md" : "border border-amber-100/70 shadow-sm"}`}
      data-testid={`lounge-${l.id}`}
      style={{ background: "linear-gradient(180deg, #faf7f0 0%, #ffffff 55%)" }}>
      {likely && (
        <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-mono uppercase tracking-[0.22em] shadow-sm" data-testid="likely-access-badge">
          <CheckCircle2 className="w-3 h-3" /> Likely access
        </div>
      )}
      <div className="p-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-amber-700 inline-flex items-center gap-1">
          <Armchair className="w-3 h-3" /> Airport Lounge
        </div>
        <h4 className="font-serif font-800 text-xl text-slate-900 leading-tight mt-1">{l.name}</h4>
        <div className="text-xs text-slate-600 mt-1.5 flex items-center gap-1 flex-wrap">
          <MapPin className="w-3 h-3 text-amber-600 shrink-0" />
          {[l.terminal, l.concourse && `Concourse ${l.concourse}`, l.gate_area,
            l.airside === true ? "Airside" : l.airside === false ? "Landside" : null]
            .filter(Boolean).join(" · ") || "Location — check airport signage"}
        </div>
        {l.description && <p className="text-sm text-slate-700 mt-3 leading-relaxed italic border-l-2 border-amber-300 pl-3">{l.description}</p>}
        {l.programs?.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-500 mb-1.5">Entry Programs</div>
            <div className="flex flex-wrap gap-1.5">
              {l.programs.map((p) => (
                <span key={p} className={`text-[11px] px-2.5 py-1 rounded-full font-700 border transition-colors
                  ${myPrograms.includes(p) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-amber-200 hover:border-amber-500"}`}>{p}</span>
              ))}
            </div>
          </div>
        )}
        {l.amenities?.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-500 mb-1.5">Amenities</div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-700">
              {l.amenities.slice(0, 8).map((a) => {
                const Icon = AMENITY_ICONS[a] || Armchair;
                return <span key={a} className="inline-flex items-center gap-1"><Icon className="w-3.5 h-3.5 text-amber-700" />{AMENITY_LABELS[a] || a}</span>;
              })}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-amber-100 text-[11px] text-slate-500">
          <span className="truncate pr-2">{l.hours || ""}{l.guest_policy ? `${l.hours ? " · " : ""}${l.guest_policy}` : ""}</span>
          <button onClick={() => onReport(l)} className="shrink-0 inline-flex items-center gap-1 text-amber-700 hover:underline font-700" data-testid={`lounge-report-${l.id}`}>
            <Flag className="w-3 h-3" /> Suggest an update
          </button>
        </div>
      </div>
    </div>
  );
}

function MyAccessEditor({ programs, myPrograms, onSave, onClose }) {
  const [sel, setSel] = useState(myPrograms);
  return (
    <div className="rounded-2xl border-2 border-amber-300 p-5 shadow-md" data-testid="my-access-editor"
      style={{ background: "linear-gradient(180deg, #faf7f0 0%, #ffffff 100%)" }}>
      <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-amber-700 inline-flex items-center gap-1.5">
        <Settings2 className="w-3.5 h-3.5" /> Personalise
      </div>
      <div className="font-serif font-800 text-xl text-slate-900 mt-1">My Lounge Access</div>
      <p className="text-sm text-slate-600 mt-1 mb-4 leading-relaxed">Select the programs and memberships you hold — lounges you can likely access get highlighted at every airport.</p>
      <div className="grid sm:grid-cols-2 gap-1.5 mb-4">
        {programs.map((p) => (
          <label key={p} className="flex items-center gap-2 text-sm bg-white border border-amber-200 rounded-lg px-3 py-2 cursor-pointer hover:border-amber-500 transition-colors">
            <input type="checkbox" checked={sel.includes(p)} data-testid={`program-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onChange={(e) => setSel(e.target.checked ? [...sel, p] : sel.filter((x) => x !== p))} />
            <span className="text-slate-800 font-600">{p}</span>
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave(sel)} data-testid="my-access-save" className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 text-sm font-700 rounded-full">Save my access</button>
        <button onClick={onClose} className="border-2 border-amber-300 hover:border-amber-500 text-amber-800 px-4 py-2 text-sm font-700 rounded-full">Cancel</button>
      </div>
    </div>
  );
}

export default function AirportLounges({ iata, onReport }) {
  const [data, setData] = useState(null);
  const [allPrograms, setAllPrograms] = useState([]);
  const [myPrograms, setMyPrograms] = useState([]);
  const [editing, setEditing] = useState(false);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    setData(null);
    api.get(`/airports/${iata}/lounges`).then((r) => setData(r.data)).catch(() => setData({ groups: [], count: 0 }));
    api.get("/lounge-programs").then((r) => setAllPrograms(r.data.programs)).catch(() => {});
    api.get("/me/lounge-access").then((r) => { setMyPrograms(r.data.programs); setSignedIn(true); }).catch(() => setSignedIn(false));
  }, [iata]);

  const save = async (sel) => {
    try {
      const r = await api.put("/me/lounge-access", { programs: sel });
      setMyPrograms(r.data.programs); setEditing(false);
      toast.success("Lounge access saved — matching lounges are now highlighted");
    } catch { toast.error("Please sign in to save your lounge access"); }
  };

  if (!data) return <div className="py-10 text-center text-slate-500 text-sm">Loading lounges…</div>;
  const likelyCount = data.groups?.reduce((n, g) => n + g.items.filter((l) => l.programs?.some((p) => myPrograms.includes(p))).length, 0) || 0;

  return (
    <div className="space-y-6" data-testid="airport-lounges-section">
      {/* Editorial header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-amber-700 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Rest & Refresh
          </div>
          <h2 className="font-serif font-800 text-3xl sm:text-4xl text-slate-900 leading-tight tracking-tight mt-1">
            Airport <span className="italic text-amber-700">Lounges</span>
          </h2>
          <p className="text-sm text-slate-600 mt-1">Curated lounges at this gateway with amenity details and program access.</p>
        </div>
        <button onClick={() => setEditing((e) => !e)} data-testid="my-access-toggle"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-700 border-2 border-amber-300 text-amber-800 hover:border-amber-500 hover:bg-amber-50 rounded-full transition-colors">
          <Settings2 className="w-4 h-4" /> My Lounge Access {myPrograms.length > 0 && `(${myPrograms.length})`}
        </button>
      </div>

      {editing && <MyAccessEditor programs={allPrograms} myPrograms={myPrograms} onSave={save} onClose={() => setEditing(false)} />}

      {myPrograms.length > 0 && data.count > 0 && (
        <p className="text-sm font-600 text-emerald-900 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg px-4 py-3" data-testid="likely-access-summary">
          {likelyCount > 0 ? `${likelyCount} lounge${likelyCount === 1 ? "" : "s"} here match your saved access — highlighted below. Access is likely, not guaranteed; verify before travel.`
            : "None of the lounges here match your saved programs — paid entry may still be available."}
        </p>
      )}
      {data.count === 0 ? (
        <div className="rounded-2xl border-2 border-amber-300 p-6 shadow-sm" data-testid="lounges-fallback"
          style={{ background: "linear-gradient(180deg, #faf7f0 0%, #ffffff 100%)" }}>
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-amber-700 mb-1">Coming soon</div>
          <p className="text-sm text-slate-800 leading-relaxed">
            Lounge research for this airport is in progress. Check the airport's official website for current lounge
            information — and if you know a lounge here, use "Suggest an update" on any airport page to help fellow travellers.
          </p>
        </div>
      ) : (
        data.groups.map((g) => (
          <div key={g.key} data-testid={`lounge-group-${g.key}`}>
            <h3 className="font-serif font-800 text-2xl text-slate-900 mb-4">
              {g.label} <span className="text-sm font-mono text-amber-700 font-500">· {g.items.length}</span>
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {g.items.map((l) => <LoungeCard key={l.id} l={l} myPrograms={myPrograms} onReport={onReport} />)}
            </div>
          </div>
        ))
      )}
      {data.count > 0 && <p className="text-xs text-slate-500 italic">{data.disclaimer}</p>}
      {!signedIn && <p className="text-xs text-slate-500 italic">Sign in to save your lounge memberships and see which lounges you can likely access at every airport.</p>}
    </div>
  );
}
